/**
 * toolPresentation — pure presentation logic shared by every rendering of a
 * backend tool event: label resolution, inline params (subtitle), summary
 * badge, group phrase, and detail availability. The detail panel itself lives
 * in RichDetails.tsx (components only, to keep Fast Refresh happy), the
 * consumer is ToolGroup.tsx.
 */

import { BACKEND_TOOL_LABELS } from '../../constants/toolLabels';
import type { BackendTool } from '../../types/websocket.types';
import { CANVAS_ACTION_CATALOG, CANVAS_RETIRED_ACTIONS, CanvasActionNameSchema, estUnVerbeCanvasRetire } from '@hector/shared-types';
import { NIGHTLY_DISPATCH_STATUS_LABEL } from '@/features/legal-wiki-builder/lib/nightlyDispatchPresentation';

// ─── Per-subagent dynamic label ─────────────────────────────────────────────

// L'orchestrateur expose un seul tool `invoke_subagent` ; seul `data.agent_name`
// distingue quel spécialiste a été invoqué. On garde le framing "Agent X" (vs
// "Recherche X") : ça signale à l'avocat qu'une instance IA dédiée a traité la
// tâche — lisible comme une feature, pas un détail technique.
export const SUBAGENT_LABELS: Record<string, string> = {
  jurisprudence_analyzer: "Agent d'analyse de jurisprudence",
  case_investigator: "Agent d'exploration du dossier",
  legal_research: 'Agent de recherche juridique',
  general_purpose: 'Agent généraliste',
  // Cascade rédaction (PR #310) : 1 instance par moyen construit en parallèle.
  moyen_builder: 'Agent de construction de moyen',
};

// ─── Per-section dynamic label (read_case_wiki) ─────────────────────────────

// `read_case_wiki` partage un nom de tool unique ; seul `data.section` distingue
// ce qui est lu. On résout ICI (comme SUBAGENT_LABELS) pour garder toolLabels.ts
// statique. `section` est présent sur started ET completed (et survit au history
// restore — c'est un arg LLM), donc le label dynamique ne reverte jamais.
const WIKI_SECTION_LABELS: Record<string, string> = {
  facts: 'Récupération des faits',
  parties: 'Récupération des parties',
  contradictions: 'Récupération des contradictions',
  pieces: 'Classification des pièces',
};

// Libellé humain d'une section (subtitle read_case_wiki) — sans le verbe d'action.
export const WIKI_SECTION_NOUNS: Record<string, string> = {
  facts: 'Faits',
  parties: 'Parties',
  contradictions: 'Contradictions',
  pieces: 'Pièces',
};

// Nom au pluriel pour le badge de comptage (« 184 faits »). Au singulier on
// retombe sur une forme générique « N éléments » si la section est inconnue.
const WIKI_SECTION_BADGE_NOUNS: Record<string, { singular: string; plural: string }> = {
  facts: { singular: 'fait', plural: 'faits' },
  parties: { singular: 'partie', plural: 'parties' },
  contradictions: { singular: 'contradiction', plural: 'contradictions' },
  pieces: { singular: 'pièce', plural: 'pièces' },
};

// Libellés humains des clés de l'objet `counts` (get_case_wiki_overview).
// Alignés sur getWikiCounts (CaseWikiRepository) : { facts, parties, contradictions, pieces }.
export const WIKI_COUNTS_LABELS: Record<string, string> = {
  facts: 'Faits',
  parties: 'Parties',
  contradictions: 'Contradictions',
  pieces: 'Pièces classées',
};

// Libellés humains des états du pipeline wiki (get_case_wiki_overview). Valeurs
// alignées sur WikiState (packages/shared-types/src/case-wiki/wiki-state.ts).
export const WIKI_STATE_LABELS: Record<string, string> = {
  never_run: 'Jamais lancée',
  running: 'En cours',
  interrupted: 'Interrompue',
  incomplete_enrichment: 'Consolidation ou analyses à relancer',
  incomplete_new_pieces: 'Nouvelles pièces à intégrer',
  up_to_date: 'À jour',
};

const CANVAS_PROGRESS_PHASE_LABELS = {
  accepted: 'Demande reçue',
  queued: 'En attente du Canvas',
  running: 'Exécution dans le Canvas',
} as const;

/** Closed, content-free Canvas operation statuses for the lawyer-facing timeline. */
const CANVAS_RECONCILIATION_ERROR_LABELS = {
  canvas_superdoc_operation_not_running: 'Résultat à vérifier',
  canvas_superdoc_mount_changed: 'Document changé',
  canvas_superdoc_probe_unavailable: 'Canvas indisponible',
  CANVAS_OPERATION_IN_PROGRESS: 'Opération Canvas en cours',
} as const;

/**
 * The progress wire is backend-private except for this closed projection. A
 * hostile or stale payload must disappear rather than render arbitrary fields
 * from a Canvas command into the lawyer-facing timeline.
 */
function canvasProgressInline(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const progress = value as Record<string, unknown>;
  const phase = progress.phase;
  if (phase !== 'accepted' && phase !== 'queued' && phase !== 'running') return null;
  const label = CANVAS_PROGRESS_PHASE_LABELS[phase];
  const completed = progress.completed_units;
  const total = progress.total_units;
  if (
    typeof completed !== 'number'
    || typeof total !== 'number'
    || !Number.isInteger(completed)
    || !Number.isInteger(total)
    || completed < 0
    || total <= 0
    || completed > total
  ) return label;
  return `${label} · ${completed}/${total}`;
}

function canvasReconciliationErrorInline(value: unknown): string | null {
  return typeof value === 'string' && value in CANVAS_RECONCILIATION_ERROR_LABELS
    ? CANVAS_RECONCILIATION_ERROR_LABELS[value as keyof typeof CANVAS_RECONCILIATION_ERROR_LABELS]
    : null;
}

export function resolveBackendToolLabel(event: BackendTool['payload']): string {
  if (event.tool === 'canvas_inspect') {
    // `preparing` is intentionally user-visible, but it must not imply that a
    // document argument or result is available to display. The stable wording
    // also keeps a restored started event visually identical to its preview.
    return 'Lecture du Canvas';
  }
  if (event.tool === 'canvas_perform_action') {
    if (event.status === 'preparing') return 'Préparation de la modification';

    // Only a known, nominal action can refine the generic label. Canvas tool
    // arguments are intentionally not present in the timeline payload.
    // Une conversation enregistrée avant le retrait d'un verbe le porte encore
    // dans sa frise : il garde le libellé qu'il avait quand l'action a eu lieu.
    const nomRecu: unknown = event.data?.action;
    if (estUnVerbeCanvasRetire(nomRecu)) return CANVAS_RETIRED_ACTIONS[nomRecu].libelle;
    const action = CanvasActionNameSchema.safeParse(nomRecu);
    if (!action.success) return 'Modification du Canvas';
    if (action.data === 'write_markdown') return 'Écriture dans le Canvas';
    return CANVAS_ACTION_CATALOG[action.data].libelle;
  }
  if (event.tool.startsWith('canvas_')) return 'Action dans le Canvas';
  if (event.tool === 'invoke_subagent') {
    const agentName = (event.data?.agent_name ?? '') as string;
    return SUBAGENT_LABELS[agentName] || BACKEND_TOOL_LABELS.invoke_subagent || 'Agent';
  }
  if (event.tool === 'read_case_wiki') {
    const section = event.data?.section;
    // Fallback sur le libellé statique si la section est absente (ex: history
    // restore d'un appel mal formé) — jamais de nom technique exposé.
    return (section && WIKI_SECTION_LABELS[section]) || BACKEND_TOOL_LABELS.read_case_wiki || 'Analyse du dossier';
  }
  if (event.tool === 'get_case_wiki_overview') {
    // never_run (`has_run === false`) → on vérifie l'existence ; sinon on évalue
    // l'état d'une analyse présente. Au `started`, `has_run` est absent → on
    // tombe dans la branche « Évaluation » (label de chargement correct).
    return event.data?.has_run === false
      ? "Vérification de l'analyse avancée"
      : "Évaluation du statut de l'analyse";
  }
  if (event.tool === 'launch_case_wiki_analysis' && event.status === 'completed') {
    return event.data?.status === 'scheduled' ? NIGHTLY_DISPATCH_STATUS_LABEL : 'Lancé';
  }
  // Un dossier de même titre et de même client existait : rien n'a été créé, la
  // ligne ne doit pas dire « Création » (même quand la reprise finit en erreur).
  if (event.tool === 'create_case' && event.data?.existing === true) {
    return 'Dossier existant retrouvé';
  }
  return BACKEND_TOOL_LABELS[event.tool] || event.tool;
}

// ─── Summary badge logic ────────────────────────────────────────────────────

export function getSummaryBadge(event: BackendTool['payload']): string | null {
  if (event.status !== 'completed') return null;
  const d = event.data;
  if (!d) return null;

  switch (event.tool) {
    // Canvas events are lifecycle telemetry. They deliberately never receive a
    // badge: even a future malformed payload cannot turn result metadata into
    // a visible timeline detail.
    case 'canvas_inspect':
    case 'canvas_perform_action':
      break;
    case 'search_jurisprudence':
    case 'jurisprudence_search':
      if (d.decisions_found !== undefined) return `${d.decisions_found} décision${d.decisions_found > 1 ? 's' : ''}`;
      break;
    case 'search_case_documents':
    case 'case_documents_search':
      if (d.chunks_count !== undefined) return `${d.chunks_count} paragraphe${d.chunks_count > 1 ? 's' : ''} lu${d.chunks_count > 1 ? 's' : ''}`;
      break;
    case 'find_case_documents':
      if (d.results_count !== undefined) return `${d.results_count} document${d.results_count > 1 ? 's' : ''}`;
      break;
    case 'read_document':
    case 'read_attached_document':
      break;
    case 'web_fetch':
      break;
    case 'legifrance_list_codes':
      if (d.count !== undefined) return `${d.count} codes`;
      break;
    case 'get_case_structure':
      if (d.children?.length) return `${d.children.length} élément${d.children.length > 1 ? 's' : ''}`;
      break;
    case 'read_case_wiki': {
      // Badge « N <noun> » keyé sur la section (« 184 faits »). `count` n'est émis
      // qu'au completed et absent sur le guard-fail (accessible:false) → on garde
      // le guard explicite pour ne pas afficher un badge cassé.
      if (d.count === undefined) break;
      const noun = d.section ? WIKI_SECTION_BADGE_NOUNS[d.section] : undefined;
      if (noun) return `${d.count} ${d.count > 1 ? noun.plural : noun.singular}`;
      return `${d.count} élément${d.count > 1 ? 's' : ''}`;
    }
    case 'get_case_wiki_overview': {
      // Vue d'ensemble : on met en avant le nombre de faits (le compteur le plus
      // parlant). never_run / inaccessible → pas de counts → pas de badge.
      const facts = d.counts?.facts;
      if (facts !== undefined) return `${facts} fait${facts > 1 ? 's' : ''}`;
      break;
    }
    case 'search_jp_by_id':
      if (d.found === true) return d.decision?.has_full_text || d.has_full_text ? 'Trouvée (+ texte)' : 'Trouvée';
      if (d.found === false) return 'Non retrouvée';
      break;
    default:
      if (d.results_count !== undefined) return `${d.results_count} résultat${d.results_count > 1 ? 's' : ''}`;
  }
  return null;
}

// ─── Inline params shown in the header for all statuses ─────────────────────

export function getInlineParams(event: BackendTool['payload']): string | null {
  const d = event.data;
  if (!d) return null;

  const parts: string[] = [];

  switch (event.tool) {
    // Canvas lifecycle telemetry is deliberately argument-free. Defend this
    // invariant at presentation time too: a malformed payload must not leak a
    // long Markdown body or a selector into the chat timeline.
    case 'canvas_inspect':
    case 'canvas_perform_action':
      if (event.status === 'in_progress') {
        const progress = canvasProgressInline(d.canvas_progress);
        if (progress !== null) parts.push(progress);
      }
      if (event.status === 'error') {
        const reconciliation = canvasReconciliationErrorInline(d.code);
        if (reconciliation !== null) parts.push(reconciliation);
      }
      break;

    case 'search_case_documents':
    case 'case_documents_search':
      if (d.query) parts.push(d.query);
      if (d.source === 'synthesis') parts.push('synthèse');
      if (d.path && d.path !== '/') parts.push(d.path);
      break;

    case 'find_case_documents':
      if (d.query) parts.push(d.query);
      if (Array.isArray(d.piece_numbers) && d.piece_numbers.length > 0) {
        parts.push(`pièce ${d.piece_numbers.join(', ')}`);
      }
      if (d.path && d.path !== '/') parts.push(d.path);
      break;

    case 'legifrance_search_code':
      if (d.query) parts.push(d.query);
      if (d.code_name) parts.push(d.code_name);
      break;

    case 'legifrance_search_legislation':
      if (d.query) parts.push(d.query);
      if (d.text_id) parts.push(d.text_id);
      break;

    case 'legifrance_get_article':
      if (d.num) parts.push(`Art. ${d.num}`);
      else if (d.articleId) parts.push(d.articleId);
      if (d.document_title) parts.push(d.document_title);
      break;

    case 'legifrance_list_codes':
      // No meaningful inline params
      break;

    case 'get_case_structure':
      if (d.path) parts.push(d.path);
      break;

    case 'web_search':
      if (d.query) parts.push(d.query);
      if (d.include_domains?.length) parts.push(d.include_domains.join(', '));
      break;

    case 'search_doctrine':
    case 'doctrine_search':
      if (d.query) parts.push(d.query);
      break;

    case 'read_document':
      if (d.document_title) parts.push(d.document_title);
      else if (d.document_id) parts.push(d.document_id);
      break;

    case 'read_attached_document':
      if (d.filename) parts.push(d.filename);
      break;

    case 'search_jp_by_id': {
      // Fallback search_params car 'started' envoie flat, 'completed' envoie wrappé
      const sp = d.search_params ?? {};
      const number = d.number ?? sp.number;
      const judilibreId = d.judilibre_id ?? sp.judilibre_id;
      const jurisdiction = d.jurisdiction ?? sp.jurisdiction;
      if (number) parts.push(`n° ${number}`);
      else if (judilibreId) parts.push(`ID: ${judilibreId}`);
      if (jurisdiction) parts.push(jurisdiction.toUpperCase());
      break;
    }

    case 'web_fetch':
      if (d.url) parts.push(d.url);
      break;

    case 'invoke_subagent':
      // Pendant le run on n'a que l'agent_name (déjà dans le label) → pas de
      // subtitle utile. Au completed, on montre les itérations comme signal
      // discret de complexité du travail effectué.
      if (typeof d.iterations === 'number' && event.status === 'completed') {
        parts.push(`${d.iterations} itération${d.iterations > 1 ? 's' : ''}`);
      }
      break;

    case 'read_case_wiki':
      // Le verbe d'action est déjà dans le label (« Récupération des … ») — le
      // subtitle redonderait. On n'affiche rien de plus ici (le badge porte le
      // compte au completed). NB: pas de `query` à pousser (read_case_wiki n'en a pas).
      break;

    case 'get_case_wiki_overview':
      // État du pipeline en subtitle (ex « up_to_date », « incomplete_new_pieces »).
      if (d.state) parts.push(WIKI_STATE_LABELS[d.state] ?? d.state);
      break;

    case 'open_objective': {
      if (typeof d.title === 'string') parts.push(d.title);
      const requirements = Array.isArray(d.requirements)
        ? d.requirements.filter((value): value is string => typeof value === 'string')
        : [];
      if (requirements.length > 0) parts.push(requirements.join(', '));
      break;
    }

    case 'close_objective': {
      const pending = Array.isArray(d.pending)
        ? d.pending.filter((value): value is string => typeof value === 'string')
        : [];
      if (pending.length > 0) {
        parts.push(`${pending.join(', ')} à terminer`);
      } else if (d.results && typeof d.results === 'object' && !Array.isArray(d.results)) {
        // Un critère fermé « bloqué » n'est pas fini : le ranger sous « vérifiés »
        // dirait à l'avocat que tout est fait alors qu'une décision l'attend.
        const blockedKeys = Array.isArray(d.blocked) ? d.blocked.map((entry) => entry.key) : [];
        const doneKeys = Object.keys(d.results).filter((key) => !blockedKeys.includes(key));
        if (doneKeys.length > 0) parts.push(`${doneKeys.join(', ')} vérifiés`);
        if (blockedKeys.length > 0) parts.push(`${blockedKeys.join(', ')} en attente`);
      }
      break;
    }

    default:
      if (d.query) parts.push(d.query);
      if (d.url) parts.push(d.url);
      break;
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

// ─── Dossier lu par l'outil, quand ce n'est pas celui de l'échange ──────────

/** Nom donné à un dossier lu dont aucun titre n'est connu (ni la liste de la conversation, ni les dossiers du cabinet de l'avocat). */
const UNLISTED_CASE_LABEL = 'Autre dossier';

/**
 * Mention « Dossier <titre> » d'une ligne d'outil qui a travaillé dans un autre
 * dossier que celui de l'échange.
 *
 * WHY : l'agent lit les dossiers connexes de la conversation sans changer de
 * principal. Sans cette mention, « Lecture de document » se lit comme une lecture
 * dans le dossier affiché par le sélecteur. Le dossier de l'échange, lui, n'est
 * jamais nommé : il est déjà à l'écran, et le répéter sur chaque ligne
 * masquerait le cas qui compte. Seule exception, `create_case` : le dossier
 * qu'il rend est l'objet de la ligne, et il devient souvent le dossier de
 * l'échange (conversation vierge) avant que la ligne soit lue, donc il est
 * toujours nommé.
 *
 * Aucune liste d'outils : tout événement qui porte `case_id` est concerné, sous
 * son nom de fil comme sous son nom persisté (le serveur pose le dossier de
 * l'appel quand ce n'est pas le principal, et l'historique le relit de la ligne
 * d'outil).
 *
 * @param event Événement d'outil, du direct ou restauré de l'historique.
 * @param shownCaseId Dossier déjà à l'écran pour cet échange ; absent pour un échange sans dossier.
 * @param caseTitleOf Titre d'un dossier d'après la liste de la conversation.
 * @returns La mention, ou `null` quand il n'y a rien à nommer.
 */
export function getCaseSubtitle(
  event: BackendTool['payload'],
  shownCaseId: string | null | undefined,
  caseTitleOf: (caseId: string) => string | undefined,
): string | null {
  // Même invariant que `getInlineParams` : la télémétrie du Canvas n'affiche
  // aucun champ de sa charge utile.
  if (event.tool.startsWith('canvas_')) return null;

  const caseId = event.data?.case_id;
  if (typeof caseId !== 'string' || caseId === '') return null;
  // Une lecture dans le dossier déjà à l'écran n'a rien à nommer ; `create_case`, si :
  // le dossier qu'elle rend est l'objet de la ligne, et il devient souvent le dossier
  // de l'échange (conversation vierge), donc « déjà à l'écran » avant même d'être lu.
  if (caseId === shownCaseId && event.tool !== 'create_case') return null;

  const title = caseTitleOf(caseId)?.trim();
  if (!title) return UNLISTED_CASE_LABEL;
  // Beaucoup de dossiers s'appellent déjà « Dossier … » : ne pas bégayer.
  return /^dossier\b/i.test(title) ? title : `Dossier ${title}`;
}

/**
 * Sous-titre complet d'une ligne d'outil : le dossier lu s'il n'est pas celui
 * de l'échange, puis les paramètres de l'appel.
 *
 * WHY le dossier en premier : le sous-titre est tronqué par l'écran quand il
 * déborde. Les paramètres se retrouvent dans le détail déplié, le dossier
 * n'est affiché nulle part ailleurs.
 */
export function getToolLineSubtitle(
  event: BackendTool['payload'],
  shownCaseId: string | null | undefined,
  caseTitleOf: (caseId: string) => string | undefined,
): string | null {
  const parts = [getCaseSubtitle(event, shownCaseId, caseTitleOf), getInlineParams(event)]
    .filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ─── Tool group summary phrase ──────────────────────────────────────────────

/**
 * Phrase résumé d'une salve de tool calls (ToolGroup). Les sous-agents
 * (invoke_subagent) ne comptent PAS dans X — décision design 2026-06-11 (deux
 * familles distinctes pour l'avocat : outils ≠ instances IA déléguées).
 *
 * Deux temps selon l'état de la salve :
 * - `inProgress` (salve ouverte, outils/sous-agents en cours) → présent
 *   « En cours d'utilisation : X outils, d'exécution : Y sous-agents » ;
 * - salve close → passé « Utilisé X outils, a exécuté Y sous-agents ».
 */
export function formatToolGroupPhrase(toolCount: number, subagentCount: number, inProgress = false): string {
  const plural = (n: number) => (n > 1 ? 's' : '');
  if (inProgress) {
    const toolsPart = `En cours d'utilisation : ${toolCount} outil${plural(toolCount)}`;
    if (toolCount > 0 && subagentCount > 0) {
      return `${toolsPart}, d'exécution : ${subagentCount} sous-agent${plural(subagentCount)}`;
    }
    if (toolCount > 0) return toolsPart;
    return `En cours d'exécution : ${subagentCount} sous-agent${plural(subagentCount)}`;
  }
  const toolsPart = `Utilisé ${toolCount} outil${plural(toolCount)}`;
  const subagentsPart = `exécuté ${subagentCount} sous-agent${plural(subagentCount)}`;
  if (toolCount > 0 && subagentCount > 0) return `${toolsPart}, a ${subagentsPart}`;
  if (toolCount > 0) return toolsPart;
  return `A ${subagentsPart}`;
}

/** Vrai tant qu'un outil n'a ni abouti ni échoué (préparation Canvas comprise). */
export function isToolRunning(status: BackendTool['payload']['status']): boolean {
  return status === 'preparing' || status === 'started' || status === 'in_progress';
}

/**
 * Suffixe live de la phrase d'un groupe d'outils : ce qui tourne en ce moment.
 *
 * WHY tous les outils en cours et pas le dernier : l'agent lance plusieurs
 * lectures et recherches de front dans une même réponse. Nommer le seul
 * dernier laissait croire qu'un outil tournait quand quatre tournaient.
 *
 * Les libellés suivent l'ordre du fil ; plusieurs appels du même libellé se
 * replient en « libellé ×N » pour que la ligne reste lisible repliée.
 *
 * @param events Outils du groupe, dans l'ordre du fil.
 * @returns Le suffixe, ou `null` si aucun outil ne tourne.
 */
export function formatRunningToolsLabel(events: readonly BackendTool['payload'][]): string | null {
  const countByLabel = new Map<string, number>();
  for (const event of events) {
    if (!isToolRunning(event.status)) continue;
    const label = resolveBackendToolLabel(event);
    countByLabel.set(label, (countByLabel.get(label) ?? 0) + 1);
  }
  if (countByLabel.size === 0) return null;
  return [...countByLabel]
    .map(([label, count]) => (count > 1 ? `${label} ×${count}` : label))
    .join(', ');
}

// ─── Objective checklist ────────────────────────────────────────────────────

export type ObjectiveChecklistItem = {
  label: string;
  state: 'todo' | 'done' | 'blocked';
  /** La mesure donnée par l'agent ; pour un critère bloqué, son motif. */
  detail?: string;
};

/**
 * Le libellé d'un critère tel que l'avocat le lit. La clé est écrite par le modèle, souvent en `snake_case`
 * (« fondements_verifies ») : on ne peut que retirer les tirets bas, pas rendre les accents.
 */
function libelleDeCritere(cle: string): string {
  const lisible = cle.replace(/_/g, ' ').trim();
  return lisible.charAt(0).toLocaleUpperCase('fr-FR') + lisible.slice(1);
}

/**
 * La liste à cocher d'un objectif, lue sur son appel d'outil : les critères annoncés à l'ouverture, puis leur état à
 * la fermeture. `null` pour tout autre outil, ou quand l'appel ne porte aucun critère.
 *
 * WHY : la ligne d'outil mettait les clés bout à bout, tronquées, et ni la mesure ni le motif d'un critère bloqué ne
 * s'affichaient nulle part — l'avocat ne pouvait pas voir ce que l'agent déclarait avoir vérifié.
 */
export function objectiveChecklist(event: BackendTool['payload']): ObjectiveChecklistItem[] | null {
  const d = event.data;
  if (!d) return null;
  if (event.tool === 'open_objective') {
    const requirements = Array.isArray(d.requirements) ? d.requirements.filter((value): value is string => typeof value === 'string') : [];
    return requirements.length === 0 ? null : requirements.map((cle) => ({ label: libelleDeCritere(cle), state: 'todo' }));
  }
  if (event.tool !== 'close_objective') return null;
  const results = d.results && typeof d.results === 'object' && !Array.isArray(d.results) ? d.results : {};
  const pending = Array.isArray(d.pending) ? d.pending.filter((value): value is string => typeof value === 'string') : [];
  const motifs = new Map((Array.isArray(d.blocked) ? d.blocked : []).map((entry) => [entry.key, entry.reason]));
  const items = [...new Set([...Object.keys(results), ...pending])].map((cle): ObjectiveChecklistItem => {
    const result = results[cle];
    const label = libelleDeCritere(cle);
    if (motifs.has(cle) || result?.status === 'blocked') {
      return { label, state: 'blocked', detail: motifs.get(cle) ?? result?.reason ?? result?.measure };
    }
    // Le serveur tranche : un critère qu'il renvoie « à terminer » n'est pas fait, quoi qu'en dise l'agent.
    const fait = result?.status === 'done' && !pending.includes(cle);
    return { label, state: fait ? 'done' : 'todo', ...(result?.measure ? { detail: result.measure } : {}) };
  });
  return items.length === 0 ? null : items;
}

/**
 * L'état le plus récent de chaque objectif d'une salve d'outils, dans l'ordre de leur ouverture : la fermeture si
 * elle y est, sinon l'ouverture. Le titre n'est porté que par l'ouverture ; il est reporté sur la fermeture.
 */
export function latestObjectiveEvents(events: ReadonlyArray<BackendTool['payload']>): Array<BackendTool['payload']> {
  const parObjectif = new Map<string, BackendTool['payload']>();
  for (const event of events) {
    if (objectiveChecklist(event) === null) continue;
    const cle = event.data?.id ?? '';
    const titre = event.data?.title ?? parObjectif.get(cle)?.data?.title;
    parObjectif.set(cle, { ...event, data: { ...event.data, ...(titre === undefined ? {} : { title: titre }) } });
  }
  return [...parObjectif.values()];
}

// ─── Detail availability ────────────────────────────────────────────────────

export function hasRichDetails(event: BackendTool['payload']): boolean {
  // Canvas payloads are lifecycle telemetry, not user-facing tool arguments.
  // Do not make an accidental future field inspectable in the timeline.
  if (event.tool === 'canvas_inspect' || event.tool === 'canvas_perform_action') return false;

  const d = event.data;
  if (!d) return false;
  if (objectiveChecklist(event) !== null) return true;
  return !!(
    d.query || d.url || d.filename || d.articleId || d.document_id || d.document_title || d.code_name || d.path ||
    d.include_domains?.length || d.source ||
    d.documents?.length || d.sources?.length || (Array.isArray(d.results) && d.results.length > 0) ||
    d.codes?.length || d.children?.length ||
    d.content_length !== undefined || d.truncated || d.error ||
    (event.tool === 'legifrance_get_article' && event.status === 'completed' && (d.num || d.document_title || d.content)) ||
    // search_jp_by_id
    d.found !== undefined || d.decision !== undefined || d.search_params !== undefined ||
    // Subagent invocation (V1) : prompt envoyé + réponse reçue + erreur éventuelle
    (event.tool === 'invoke_subagent' && (d.prompt || d.response || d.error_message)) ||
    // Case Wiki overview : état / compteurs / pièces analysées
    (event.tool === 'get_case_wiki_overview' && (d.state || d.counts || d.total_pieces !== undefined)) ||
    // Case Wiki section read : section / mode / nombre lu
    (event.tool === 'read_case_wiki' && (d.section || d.mode || d.count !== undefined))
  );
}
